import cloneDeep from 'lodash/cloneDeep';
import {
    buildFacetFilters,
    createFacetFiltersFromFilterExpression,
} from '../../utils/facet-filters.js';

let filters = {};
let facetFilters = [];
let sortBy = null;

// Debounce helper to prevent race conditions
let searchDebounceTimer = null;
let requestCounter = 0;
const debounceSearch = (fn, delay = 500) => {
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = setTimeout(fn, delay);
};

export const searchproductlist = {
    name: 'searchproductlist',
    component() {
        return {
            collection: null,
            isCollection: null,
            filters: {},
            facetFilters: [],
            products: [],
            totalResults: null,
            currentResults: null,
            currentResultsCount: 0,
            page: 0,
            isInit: false,
            query: null,
            sortBy: cloneDeep(window.defaultSort),
            haveLoadedAllProducts: false,
            productsPerPage: 8,
            initialPageSize: 8,
            init() {
                this.extractFiltersFromURL();
                this.syncFacetFilters();
            },
            buildFacetFilters(sourceFilters = filters) {
                return buildFacetFilters(sourceFilters);
            },
            createFacetFiltersFromFilterExpression(filterExpression = '') {
                return createFacetFiltersFromFilterExpression(filterExpression);
            },
            syncFacetFilters() {
                this.facetFilters = this.buildFacetFilters();
                facetFilters = cloneDeep(this.facetFilters);
                return this.facetFilters;
            },
            initialise(collectionHandle = null, isCollection = true, productsPerPage = 8) {
                const currentFacetFilters = this.syncFacetFilters();
                const params = new URLSearchParams(window.location.search);
                const pageFromUrl = Number(params.get('page')) || 1;

                this.productsPerPage = productsPerPage;
                this.initialPageSize = Math.max(this.productsPerPage, pageFromUrl * this.productsPerPage);

                if (isCollection) {
                    this.collection = collectionHandle;
                    this.isCollection = isCollection;
                    const hasActiveFilters = Object.keys(filters).some(key => filters[key] && filters[key].length > 0);

                    if (hasActiveFilters) {
                        window.dispatchEvent(new CustomEvent('searchrequest', {
                            detail: {
                                type: 'plp',
                                index: this.sortBy,
                                filters: `collections:${this.collection}`,
                                facetFilters: currentFacetFilters,
                                isFiltering: true,
                                productsPerPage: this.initialPageSize
                            }
                        }));
                    } else {
                        window.dispatchEvent(new CustomEvent('searchrequest', {
                            detail: {
                                type: 'plp',
                                index: this.sortBy,
                                filters: `collections:${this.collection}`,
                                facetFilters: currentFacetFilters,
                                productsPerPage: this.initialPageSize
                            }
                        }));
                    }
                } else {
                    const urlParams = new URLSearchParams(window.location.search);
                    const queryFromParams = urlParams.get('q');
                    
                    // Set flag to prevent React components from also dispatching initial search
                    window.alpineHandlingInitialSearch = true;
                    setTimeout(() => {
                        window.alpineHandlingInitialSearch = false;
                    }, 2000);
                    
                    this.query(queryFromParams, null);
                }

                // searchrequest-sortchanged
                window.addEventListener('collection-search-loaded', this.handleCollectionSearchLoad(this));
                window.addEventListener('sort-products', this.sort.bind(this));
                window.addEventListener('searchrequest-products-complete', this.handleSearchComplete.bind(this));
                window.addEventListener('nextpage', this.handleNextPage.bind(this));
                window.addEventListener('quicklinks', this.quickLinks.bind(this));
                
                // Handle browser back/forward navigation
                window.addEventListener('popstate', this.handlePopState.bind(this));
            },
            subscribe(callback) {
                window.addEventListener('products-results', callback);
                return () => {
                    window.removeEventListener('products-results', callback);
                };
            },
            handleCollectionSearchLoad() {
                if (filters) {
                    if (this.isCollection) {
                        window.dispatchEvent(new CustomEvent('active-filters', {
                            detail: {
                                type: 'plp',
                                activeFilters: filters
                            }
                        }));
                    } else {
                        window.dispatchEvent(new CustomEvent('active-filters', {
                            detail: {
                                type: 'search',
                                activeFilters: filters
                            }
                        }));
                    }
                }
            },
            query(input, lastQuery = localStorage.getItem('queryInput')) {
                requestCounter++;
                
                // Use debounce to prevent rapid-fire search requests
                debounceSearch(() => {
                    const currentFacetFilters = this.syncFacetFilters();

                    if (lastQuery) {
                        this.query = lastQuery;
                        const queryObj = {
                            'query': lastQuery,
                            'type': 'search',
                            'index': this.sortBy,
                            'filters': '',
                            'facetFilters': currentFacetFilters,
                            'source': `query-with-lastQuery-${requestCounter}`,
                            'productsPerPage': this.initialPageSize
                        }
                        dispatchEvent(new CustomEvent('searchrequest', { detail: queryObj }))
                    } else {
                        this.query = input;
                        const queryObj = {
                            'query': input,
                            'type': 'search',
                            'index': this.sortBy,
                            'filters': '',
                            'facetFilters': currentFacetFilters,
                            'source': `query-input-${requestCounter}`,
                            'productsPerPage': this.initialPageSize
                        }
                        dispatchEvent(new CustomEvent('searchrequest', { detail: queryObj }))
                    }
                }, 250);
            },
            filter(filterKey, valueKey) {
                requestCounter++;
                
                if (filters[filterKey]) {
                    if (filters[filterKey].includes(valueKey)) {
                        // Remove the filter if it already exists
                        filters[filterKey] = filters[filterKey].filter(value => value !== valueKey);
                    } else {
                        // Add the filter if it doesn't exist
                        filters[filterKey].push(valueKey);
                    }
                } else {
                    // Initialize the filter if it doesn't exist
                    filters[filterKey] = [valueKey];
                }

                if (this.isCollection) {
                    window.dispatchEvent(new CustomEvent('active-filters', {
                        detail: {
                            type: 'plp',
                            activeFilters: filters
                        }
                    }));
                } else {
                    window.dispatchEvent(new CustomEvent('active-filters', {
                        detail: {
                            type: 'search',
                            activeFilters: filters
                        }
                    }));
                }

                const currentFacetFilters = this.syncFacetFilters();

                this.updateUrl();

                if (this.isCollection) {
                    window.dispatchEvent(new CustomEvent('searchrequest-filter-applied', {
                        detail: {
                            type: 'plp',
                            index: this.sortBy,
                            filters: `collections:${this.collection}`,
                            facetFilters: currentFacetFilters,
                            reset: true,
                            isFiltering: true,
                            source: `filter-collection-${requestCounter}`
                        }
                    }));
                } else {
                    window.dispatchEvent(new CustomEvent('searchrequest-filter-applied', {
                        detail: {
                            type: 'search',
                            index: this.sortBy,
                            query: this.query,
                            filters: '',
                            facetFilters: currentFacetFilters,
                            source: `filter-search-${requestCounter}`,
                            isFiltering: true,
                        }
                    }));
                }

                this.totalResults = 0;
                this.currentResults = null;
                this.currentResultsCount = 0;
                this.page = 0;
            },
            sort(event) {
                requestCounter++;
                
                this.sortBy = event.detail.sortOption;
                sortBy = this.sortBy;
                const currentFacetFilters = this.syncFacetFilters();

                this.updateUrl();

                if (this.isCollection) {
                    window.dispatchEvent(new CustomEvent('searchrequest-sort-applied', {
                        detail: {
                            type: 'plp',
                            index: this.sortBy,
                            filters: `collections:${this.collection}`,
                            facetFilters: currentFacetFilters,
                            reset: true,
                            source: `sort-collection-applied-${requestCounter}`,
                            productsPerPage: this.initialPageSize
                        }
                    }));
                } else {
                    window.dispatchEvent(new CustomEvent('searchrequest-sort-applied', {
                        detail: {
                            type: 'search',
                            index: this.sortBy,
                            query: this.query,
                            filters: '',
                            facetFilters: currentFacetFilters,
                            source: `sort-search-applied-${requestCounter}`,
                            productsPerPage: this.initialPageSize
                        }
                    }));
                }

                this.totalResults = 0;
                this.currentResults = null;
                this.currentResultsCount = 0;
                this.page = 0;
            },
            handleSearchComplete(event) {
                const { type, page, isFiltering, query, products, totalResults, filters: eventFilters } = event.detail;
                const currentFacetFilters = this.syncFacetFilters();
                                
                if (type === 'plp' || type === 'search') {
                    if (this.page > 0 && isFiltering === false) {
                        this.products = [...this.currentResults, ...products];
                        this.currentResults = this.products;
                        this.currentResultsCount = this.currentResults.length;
                    } else {
                        this.products = products;
                        this.currentResults = products;
                        this.currentResultsCount = this.currentResults.length;
                    }

                    this.filters = eventFilters.reduce((acc, filter) => {
                        acc[filter.key] = [];
                        return acc;
                    }, {});

                    this.totalResults = totalResults;
                    this.haveLoadedAllProducts = this.totalResults === this.currentResults.length;

                    if (this.isCollection) {
                        window.dispatchEvent(new CustomEvent('product-list', {
                            detail: {
                                type: 'plp',
                                products: this.products,
                                filters: this.filters,
                                facetFilters: currentFacetFilters,
                                totalResults: this.totalResults,
                                page,
                                isFiltering
                            }
                        }));
                    } else {
                        window.dispatchEvent(new CustomEvent('product-list', {
                            detail: {
                                type: 'search',
                                products: this.products,
                                filters: this.filters,
                                facetFilters: currentFacetFilters,
                                query: this.query,
                                totalResults: this.totalResults
                            }
                        }));

                        // Update the document title dynamically with correct totalResults and shop title
                        const shopName = window.shopName;
                        document.title = `${this.totalResults} results for "${this.query}" - ${shopName}`;
                    }
                }
            },
            handlePopState() {
                // Check if React is already handling this popstate event
                if (window.reactHandlingPopstate) {
                    return;
                }
                
                // Set flag to prevent React from also handling this
                window.alpineHandlingPopstate = true;
                setTimeout(() => {
                    window.alpineHandlingPopstate = false;
                }, 1000);
                
                requestCounter++;
                
                // Add small delay to prevent race conditions with React components
                setTimeout(() => {
                    // Parse current URL parameters
                    const urlParams = new URLSearchParams(window.location.search);
                    const queryFromParams = urlParams.get('q');
                    const filtersFromParams = urlParams.get('filters');
                    const sortFromParams = urlParams.get('sort_by');
                    
                    // Update internal state
                    if (queryFromParams) {
                        this.query = queryFromParams;
                    }
                    if (sortFromParams) {
                        this.sortBy = sortFromParams;
                    } else {
                        // Reset to default sort when no sort parameter
                        this.sortBy = window.defaultSort || 'relevance';
                    }
                    
                    // Parse and update filters
                    if (filtersFromParams) {
                        try {
                            const decodedFilters = decodeURIComponent(filtersFromParams);
                            
                            // IMPORTANT: Reconstruct the global filters object from the URL state
                            // This ensures the global filters object stays in sync with the URL
                            filters = {}; // Clear the global filters object
                            
                            if (decodedFilters) {
                                // Use same parsing logic as extractFiltersFromURL to handle OR/AND logic
                                // Split by AND to get different categories
                                const categoryGroups = decodedFilters.split(' AND ');
                                
                                categoryGroups.forEach(group => {
                                    // Remove parentheses if present
                                    const cleanGroup = group.replace(/^\(|\)$/g, '');
                                    
                                    // Split by OR to get values within the same category
                                    const orFilters = cleanGroup.split(' OR ');
                                    
                                    orFilters.forEach(filter => {
                                        // Handle both quoted and unquoted values
                                        let match = filter.match(/^(.+?):"(.+?)"$/) || filter.match(/^(.+?):'(.+?)'$/);
                                        if (match) {
                                            const [, key, value] = match;
                                            if (key && value) {
                                                if (!filters[key]) {
                                                    filters[key] = [];
                                                }
                                                if (!filters[key].includes(value)) {
                                                    filters[key].push(value);
                                                }
                                            }
                                        } else {
                                            // Pattern 2: key:value (no quotes, no spaces)
                                            const [key, value] = filter.split(':');
                                            if (key && value) {
                                                // Remove quotes from value if present for backward compatibility
                                                const cleanValue = value.replace(/^['"]|['"]$/g, '');
                                                if (!filters[key]) {
                                                    filters[key] = [];
                                                }
                                                if (!filters[key].includes(cleanValue)) {
                                                    filters[key].push(cleanValue);
                                                }
                                            }
                                        }
                                    });
                                });
                            }
                            
                            // Sync facet filters for consistency
                            this.syncFacetFilters();
                        } catch (e) {
                            console.warn('Failed to decode filters:', e);
                            filters = {}; // Clear on error
                            this.syncFacetFilters();
                        }
                    } else {
                        filters = {}; // Clear the global filters object when no filters in URL
                        this.syncFacetFilters();
                    }

                    const currentFacetFilters = this.syncFacetFilters();
                    
                    // Determine what type of request to dispatch based on context
                    if (this.isCollection) {
                        // For collection pages, dispatch collection-specific events
                        if (sortFromParams) {
                            // Dispatch sort event for collections
                            window.dispatchEvent(new CustomEvent('searchrequest-sort-applied', {
                                detail: {
                                    type: 'plp',
                                    index: this.sortBy,
                                    filters: `collections:${this.collection}`,
                                    facetFilters: currentFacetFilters,
                                    reset: true,
                                    source: `popstate-sort-collection-${requestCounter}`,
                                    productsPerPage: this.initialPageSize
                                }
                            }));
                        } else if (filtersFromParams) {
                            // Dispatch filter event for collections
                            window.dispatchEvent(new CustomEvent('searchrequest-filter-applied', {
                                detail: {
                                    type: 'plp',
                                    index: this.sortBy,
                                    filters: `collections:${this.collection}`,
                                    facetFilters: currentFacetFilters,
                                    reset: true,
                                    isFilteringRequest: true,
                                    source: `popstate-filter-collection-${requestCounter}`
                                }
                            }));
                        } else {
                            // Default collection reload - dispatch searchrequest to reload the collection
                            window.dispatchEvent(new CustomEvent('searchrequest', {
                                detail: {
                                    type: 'plp',
                                    index: this.sortBy || window.defaultSort || 'popularity',
                                    filters: `collections:${this.collection}`,
                                    facetFilters: currentFacetFilters,
                                    reset: true,
                                    source: `popstate-collection-reload-${requestCounter}`,
                                    productsPerPage: this.initialPageSize
                                }
                            }));
                        }
                    } else if (queryFromParams) {
                        // For search pages, always dispatch search request when there's a query
                        // This handles all cases: sort changes, filter changes, parameter removal, etc.
                        window.dispatchEvent(new CustomEvent('searchrequest', {
                            detail: {
                                query: queryFromParams,
                                type: 'search',
                                index: this.sortBy || window.defaultSort || 'relevance',
                                filters: '',
                                facetFilters: currentFacetFilters,
                                source: `alpine-search-popstate-${requestCounter}`,
                                productsPerPage: this.initialPageSize
                            }
                        }));
                    }
                }, 150);
            },
            quickLinks(event) {
                const { url , collectionHandle } = event.detail;
                const quickLinkFilterString = url ? decodeURIComponent(decodeURIComponent(url)) : '';
                const quickLinkFacetFilters = this.createFacetFiltersFromFilterExpression(quickLinkFilterString);

                this.page = 0;

                window.dispatchEvent(new CustomEvent('searchrequest', {
                    detail: {
                        type: 'plp',
                        index: this.sortBy || 'popularity', // or get from current sort
                        filters: `collections:${collectionHandle}`,
                        facetFilters: quickLinkFacetFilters,
                        isFiltering: true,
                        productsPerPage: this.initialPageSize
                    }
                }));
            },
            handleNextPage(event) {
                const { page } = event.detail;

                if (this.haveLoadedAllProducts) {
                    window.dispatchEvent(new CustomEvent('product-list', {
                        detail: {
                            type: 'plp',
                            products: this.products,
                            filters: this.filters,
                            totalResults: this.totalResults,
                            page,
                            isFiltering: false
                        }
                    }));

                    return;
                }

                this.page = event.detail.page;

                if (this.isCollection) {
                    window.dispatchEvent(new CustomEvent('searchrequest-nextpage', {
                        detail: {
                            type: 'plp',
                            filters: `collections:${this.collection}`,
                            facetFilters: this.facetFilters,
                            index: this.sortBy,
                            page: this.page
                        }
                    }));

                } else {
                    const queryObj = {
                        'query': this.query,
                        'type': 'search',
                        'filters': '',
                        'facetFilters': this.facetFilters,
                        'index': this.sortBy,
                        'page': this.page
                    }
                    dispatchEvent(new CustomEvent('searchrequest-nextpage', { detail: queryObj }))
                }
            },
            extractFiltersFromURL() {
                const urlParams = new URLSearchParams(window.location.search);
                const filterString = urlParams.get('filters');
                const sortBy = urlParams.get('sort_by');

                // Clear existing filters first to ensure clean state
                filters = {};

                if (filterString) {
                    // Handle potential double URL encoding
                    let decodedParams = decodeURIComponent(filterString);
                    // Check if it needs another decode (for cases like %253A -> %3A -> :)
                    if (decodedParams.includes('%')) {
                        try {
                            decodedParams = decodeURIComponent(decodedParams);
                        } catch (e) {
                            console.warn('Second URL decode failed, using single decode:', e);
                        }
                    }
                    
                    // Split by AND to get different categories
                    const categoryGroups = decodedParams.split(' AND ');
                    
                    categoryGroups.forEach(group => {
                        // Remove parentheses if present
                        const cleanGroup = group.replace(/^\(|\)$/g, '');
                        
                        // Split by OR to get values within the same category
                        const orFilters = cleanGroup.split(' OR ');
                        
                        orFilters.forEach(filter => {
                            const colonIndex = filter.indexOf(':');
                            if (colonIndex > 0) {
                                const key = filter.substring(0, colonIndex);
                                let value = filter.substring(colonIndex + 1);
                                
                                // Remove quotes from value if present (single or double)
                                value = value.replace(/^['"]|['"]$/g, '');
                                                                
                                if (filters[key]) {
                                    filters[key].push(value);
                                } else {
                                    filters[key] = [value];
                                }
                            }
                        });
                    });

                } else {
                    // Clear filters when no filter parameter
                    filters = {};
                }

                if (sortBy) {
                    this.sortBy = sortBy;
                    window.extractedSortBy = cloneDeep(sortBy);
                }

                this.syncFacetFilters();

                window.dispatchEvent(new CustomEvent('extracted-params', {
                    detail: {
                        index: this.sortBy
                    }
                }));
            },
            updateUrl(){
                // Build filter string with AND logic between categories, OR within categories
                const filterStringUrl = Object.keys(filters)
                    .filter(key => filters[key] && filters[key].length > 0) // Filter out empty arrays
                    .map(key => {
                        const categoryFilters = filters[key].map(value => {
                            // Quote values that contain spaces or special characters for URL
                            if (value.includes(' ') || value.includes(':') || value.includes('\'') || value.includes('"')) {
                                return `${key}:"${value}"`;
                            } else {
                                return `${key}:${value}`;
                            }
                        }).join(' OR ');
                        // If multiple values in same category, wrap in parentheses
                        return filters[key].length > 1 ? `(${categoryFilters})` : categoryFilters;
                    })
                    .filter(Boolean)
                    .join(' AND ');

                const url = new URL(window.location);

                if (filterStringUrl) {
                    const encodedFilterString = encodeURIComponent(filterStringUrl);
                    url.searchParams.set('filters', encodedFilterString);
                } else {
                    url.searchParams.delete('filters');
                }

                if (sortBy) {
                    url.searchParams.set('sort_by', sortBy);
                } else {
                    url.searchParams.delete('sort_by');
                }

                window.history.pushState({}, '', url);
            },
            clear() {
                filters = {};
                facetFilters = [];
                this.facetFilters = [];

                this.currentResults = null;
                this.currentResultsCount = 0;
                this.page = 0;

                // Clear URL parameters
                const url = new URL(window.location);
                url.searchParams.delete('filters');
                window.history.pushState({}, '', url);

                if (this.isCollection) {
                    window.dispatchEvent(new CustomEvent('searchrequest-filter-applied', {
                        detail: {
                            type: 'plp',
                            index: this.sortBy,
                            filters: `collections:${this.collection}`,
                            facetFilters: this.facetFilters,
                            reset: true,
                            isFiltering: true
                        }
                    }));
                } else {
                    window.dispatchEvent(new CustomEvent('searchrequest-filter-applied', {
                        detail: {
                            type: 'search',
                            query: this.query,
                            index: this.sortBy,
                            filters: '',
                            facetFilters: this.facetFilters,
                            isFiltering: true
                        }
                    }));
                }

                this.filters = {};

                // Dispatch clear event to notify components
                window.dispatchEvent(new CustomEvent('filters-cleared'));

                if (this.isCollection) {
                    window.dispatchEvent(new CustomEvent('active-filters', {
                        detail: {
                            type: 'plp',
                            activeFilters: filters
                        }
                    }));
                } else {
                    window.dispatchEvent(new CustomEvent('active-filters', {
                        detail: {
                            type: 'search',
                            activeFilters: filters
                        }
                    }));
                }
            }    
        }
    }
}

export default searchproductlist;